/**
 * Raw WebSocket transport (web-client.md §2). Wraps the platform `WebSocket` global (present in
 * browsers and Node 22). DOM-free: it touches no `document`/`window`.
 */
import type { CloseInfo, Transport, TransportFactory } from './transport.ts';

const noop = (): void => {};

export class RawWebSocketTransport implements Transport {
  readonly url: string;
  private readonly ws: WebSocket;
  private openHandler: () => void = noop;
  private messageHandler: (data: string) => void = noop;
  private closeHandler: (info: CloseInfo) => void = noop;
  private errorHandler: (error: unknown) => void = noop;
  private locallyClosed = false;

  constructor(url: string) {
    this.url = url;
    this.ws = new WebSocket(url);
    this.ws.addEventListener('open', () => this.openHandler());
    this.ws.addEventListener('message', (ev: MessageEvent) => {
      const data = typeof ev.data === 'string' ? ev.data : String(ev.data);
      this.messageHandler(data);
    });
    this.ws.addEventListener('error', (ev: Event) => this.errorHandler(ev));
    this.ws.addEventListener('close', (ev: CloseEvent) =>
      this.closeHandler({ code: ev.code, reason: ev.reason, wasClean: this.locallyClosed }),
    );
  }

  onOpen(handler: () => void): void {
    this.openHandler = handler;
  }
  onMessage(handler: (data: string) => void): void {
    this.messageHandler = handler;
  }
  onClose(handler: (info: CloseInfo) => void): void {
    this.closeHandler = handler;
  }
  onError(handler: (error: unknown) => void): void {
    this.errorHandler = handler;
  }

  send(data: string): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  close(code?: number, reason?: string): void {
    this.locallyClosed = true;
    if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
      this.ws.close(code, reason);
    }
  }
}

export const rawWebSocketTransportFactory: TransportFactory = (url) =>
  new RawWebSocketTransport(url);
